import { inject, Injectable } from '@angular/core';
import {
  combineLatest,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  map,
  Observable,
  shareReplay,
  Subject,
  tap,
  throttleTime
} from 'rxjs';
import { Settings } from '../settings/settings.service';
import { MatrixService } from '../matrix/matrix.service';
import { SyncService } from '../sync/sync.service';
import { SessionService } from '../session/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
/**
 * Service für Typing-Benachrichtigungen.
 * Outbound: Versendet Typing-Status an den Server.
 * Inbound: Streamt die tippenden Nutzer des aktuellen Raums als Observable.
 * A_25431 - Eingabebenachrichtigungen
 */
export class TypingService {
  readonly typingUsers$: Observable<string[]>;
  private readonly typingInput$ = new Subject<{
    message: string;
    roomId: string;
  }>();

  private readonly matrixService = inject(MatrixService);
  private readonly syncService = inject(SyncService);
  private readonly sessionService = inject(SessionService);

  constructor() {
    this.typingUsers$ = combineLatest([this.syncService.typing$, this.sessionService.getSessionRoomId$()]).pipe(
      filter(([typing, roomId]) => typing.roomId === roomId),
      map(([typing]) => typing.members),
      distinctUntilChanged((a, b) => a.length === b.length && a.every((v, i) => v === b[i])),
      shareReplay(1)
    );

    this.typingInput$
      .pipe(
        tap(({ message, roomId }) => {
          if (message.trim() === '') {
            return from(this.cancelTypingNotification(roomId));
          }
          return EMPTY;
        }),
        throttleTime(2000, undefined, {
          leading: true,
          trailing: false
        }),

        concatMap(({ message, roomId }) => {
          if (message.trim() != '') {
            return from(this.sendTypingNotification(roomId));
          }
          return EMPTY;
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  public async checkForTyping(message: string, roomId: string): Promise<void> {
    this.typingInput$.next({ message, roomId });
  }

  /**
   * Versendet eine Eingabebenachrichtigung, falls diese in den Einstellungen aktiviert ist.
   * A_25431 - Eingabebenachrichtigungen
   */
  async sendTypingNotification(roomId: string): Promise<void> {
    if (Settings.getInstance().settingsConfigurations.typingNotifications) {
      await this.matrixService.client.sendTyping(roomId, true, 30000);
    }
  }

  /**
   * Beendet die Eingabebenachrichtigung.
   * A_25431 - Eingabebenachrichtigungen
   */
  async cancelTypingNotification(roomId: string): Promise<void> {
    if (Settings.getInstance().settingsConfigurations.typingNotifications) {
      await this.matrixService.client.sendTyping(roomId, false, 30000);
    }
  }
}
